"use client";
import React, { useLayoutEffect, useState } from "react";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  Skeleton,
  useMediaQuery,
} from "@mui/material";

import Image from "next/image";
import Link from "next/link";

import MenuIcon from "@mui/icons-material/MenuRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutwardRounded";

import { FooterLinkButton } from "./Footer";

import { getCruiseOperators } from "@/services/backOffice";
import CallNowButton from "../common/CallNowButton";
import { HomeContactButtons } from "../home-redesigned/HomeFAQs";
import { OperatorDetail } from "@/interfaces/ICruisesOperators";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
// import WhatsAppButton from "../icons/WhatsApp";
import MailIconButton from "../icons/MailIcon";
import { destinationList } from "../destinations/AllDestinations";
import { useAppDispatch } from "@/libs/hooks";
import { setUserAgentAuto } from "@/slices/appSlice";

// const cruiseLines = [
// 	"Azamara Cruises",
// 	"Carnival",
// 	"Celebrity Cruises",
// 	"Costa",
// 	"Cunard Line",
// 	"Disney Cruise Line",
// 	"Holland America Line",
// 	"MSC Cruises",
// 	"Norwegian Cruise Line",
// 	"Oceania Cruises",
// 	"Princess Cruises",
// 	"Regent Cruises",
// 	"Royal Caribbean Cruises",
// 	"Virgin Voyages",
// 	"All Other Cruise Lines",
// ];

const Header: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [operatorData, setOperatorData] = useState<OperatorDetail[]>([]);

  const dispatch = useAppDispatch();

  useLayoutEffect(() => {
    async function operatorMGM() {
      try {
        const { data } = await getCruiseOperators(45, 1, true);

        setOperatorData(data?.operatorDetails ?? []);
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        setIsLoading(false);
      }
    }
    operatorMGM();
    dispatch(setUserAgentAuto());
  }, []);

  const [openMegaMenu, setOpenMegaMenu] = useState(false);
  const [menuOpenDestination, setMenuOpenDestination] = useState(false);
  const [menuOpenCruiseline, setMenuOpenCruiseline] = useState(false);

  const isDesktop = useMediaQuery("(min-width:900px)");

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: "#fff",
          boxShadow: "unset",
          minHeight: "unset",
          zIndex: 1100,
        }}
      >
        <Container sx={{ py: "14px" }}>
          <Box
            sx={{
              display: { xs: "flex", md: "grid" },
              gridTemplateColumns: "200px 1fr 200px",
              "@media screen and (max-width:1350px)": {
                gridTemplateColumns: "200px 1fr",
              },
              gap: "10px",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "#fff",
            }}
          >
            <Link href="/">
              <Box sx={{ position: "relative", cursor: "pointer" }}>
                <Image
                  src="/images/logo/wcv-logo-edited.png"
                  alt="World Cruise Vibes"
                  width={180}
                  height={45}
                />
              </Box>
            </Link>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-around",
                "@media screen and (max-width:1350px)": {
                  display: "none",
                  justifyContent: "space-between",
                },
                gap: "4px",
                bgcolor: "#EDF1FF",
                color: "#060273",
                width: "100%",
                maxWidth: "910px",
                // maxWidth: "750px",
                justifySelf: "center",
                p: "9px 15px",
                borderRadius: "12px",
                fontWeight: 700,
                fontFamily: "'blauerNue', 'blauerNue Fallback'",
                ".MuiDivider-root": {
                  my: "0px",
                },
                "> .header-link": {
                  fontSize: { xs: "14px", lg: "14px", xl: "14px" },
                  cursor: "pointer",
                  p: "6px 4px",
                  letterSpacing: "0px",
                },
              }}
            >
              <Box className="header-link">
                <Link href="/cruise-destinations">Destinations</Link>
              </Box>

              <Divider orientation="vertical" variant="middle" flexItem />

              <Box
                className="header-link"
                sx={{ position: "relative" }}
                onMouseEnter={() => setOpenMegaMenu(true)}
                onMouseLeave={() => setOpenMegaMenu(false)}
              >
                <Box
                  sx={{
                    "&:before": {
                      position: "absolute",
                      content: '""',
                      height: "50px",
                      width: "113px",
                      left: "-10px",
                      zIndex: 1,
                    },
                  }}
                >
                  Cruise Line
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    transform: `${!openMegaMenu ? "scale(0)" : "scale(1)"} translateX(-50%)`,
                    transition: "0.3s",
                    backgroundColor: "#ffffff",
                    boxShadow: "0px 2px 16px rgba(0 0 0 / 0.2)",
                    borderRadius: "14px",
                    padding: "14px",
                    fontSize: "14px",
                    transformOrigin: "left top",
                    top: "35px",
                    left: "50%",
                    "&:before": {
                      content: '" "',
                      position: "absolute",
                      borderLeft: "10px solid transparent",
                      borderRight: "10px solid transparent",
                      borderBottom: "12px solid #fff",
                      borderTopLeftRadius: "50px",
                      color: "#fff",
                      top: "-11px",
                      left: "50%",
                      transform: "translateX(-50%)",
                    },
                  }}
                >
                  {openMegaMenu && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row-reverse",
                        gap: "14px",
                        maxHeight: "310px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr",
                          gap: "10px",
                          a: {
                            height: "fit-content",
                          },
                          height: "100%",
                          maxHeight: "310px",
                          overflow: "auto",
                        }}
                      >
                        {isLoading ? (
                          <>
                            {[...new Array(5)].map((_, index) => (
                              <Box key={index}>
                                <MegaMenuSkeleton />
                              </Box>
                            ))}
                          </>
                        ) : (
                          <>
                            {(operatorData?.length ?? 0) > 0 ? (
                              operatorData?.map((operator, index) => (
                                <Link
                                  key={index}
                                  href={`/best-cruise-lines/${operator?.id}`}
                                  style={{ display: "grid" }}
                                >
                                  <Box
                                    sx={{
                                      p: "8px 15px 8px 12px",
                                      bgcolor: "#edf1ff",
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      gap: "10px",
                                      borderRadius: "10px",
                                      border: "1px solid #edf1ff",
                                      transition: "all 0.1s",
                                      "&:hover": {
                                        bgcolor: "#92aaff",
                                        border: "1px solid #6284ff",
                                      },
                                      svg: {
                                        color: "primary.main",
                                      },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: "20px",
                                        width: "100%",
                                      }}
                                    >
                                      <Box sx={{ width: "100%" }}>
                                        <Box
                                          sx={{
                                            fontSize: "16px",
                                            fontWeight: 600,
                                            fontFamily: "'blauerNue', 'blauerNue Fallback'",
                                          }}
                                        >
                                          {operator?.title}{" "}
                                        </Box>
                                        <Box
                                          sx={{
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            mt: "4px",
                                          }}
                                        >
                                          Operates {operator.shipsCount} Ships
                                        </Box>
                                      </Box>

                                      <Box
                                        sx={{
                                          border: "1px solid #BFCDFF77",
                                          p: "6px 10px",
                                          bgcolor: "#fff",
                                          borderRadius: "8px",
                                          img: {
                                            objectFit: "contain",
                                            width: "120px",
                                            height: "auto",
                                            maxHeight: "50px",
                                            borderRadius: "4px",
                                            display: "flex",
                                          },
                                        }}
                                      >
                                        <Image
                                          src={operator?.profileImageHref}
                                          alt={operator?.title}
                                          width={100}
                                          height={50}
                                          loader={({ src }) => src}
                                        />
                                      </Box>
                                    </Box>
                                    <Box
                                      sx={{
                                        height: "100%",
                                        borderRadius: "8px",
                                        display: "grid",
                                        placeItems: "center",
                                      }}
                                    >
                                      <ArrowOutwardIcon />
                                    </Box>
                                  </Box>
                                </Link>
                              ))
                            ) : (
                              <Alert severity="error" sx={{ width: "400px" }}>
                                No data available
                              </Alert>
                            )}
                          </>
                        )}
                      </Box>

                      <Box
                        sx={{
                          position: "relative",
                          width: "400px",
                          minHeight: "310px",
                          height: "100%",
                        }}
                      >
                        <Image
                          src="https://images.unsplash.com/photo-1580541631950-7282082b53ce?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTR8fGNydWlzZXxlbnwwfHwwfHx8MA%3D%3D"
                          alt="Cruise Lines"
                          fill
                          style={{
                            objectFit: "cover",
                            borderRadius: "10px",
                          }}
                          loader={({ src }) => src}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: "0px",
                            left: "0px",
                            zIndex: 2,
                            bgcolor: "#fff",
                            p: "3px 6px 3px 3px",
                            "&:before": {
                              content: '""',
                              width: "19px",
                              height: "100%",
                              backgroundColor: "#fff",
                              position: "absolute",
                              top: 0,
                              right: "-10px",
                              bottom: "1px",
                              transform: "skew(31deg)",
                              zIndex: -2,
                              borderTopRightRadius: "4px",
                            },
                          }}
                        >
                          <Link href={`/best-cruise-lines`}>
                            <Box sx={{ position: "relative", left: "4px" }}>
                              View all Cruise lines{" "}
                              <OpenInNewIcon
                                sx={{ fontSize: "17px", position: "relative", top: "4px" }}
                              />
                            </Box>
                          </Link>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* <Divider orientation="vertical" variant="middle" flexItem />

              <Box className="header-link">
                <Link href="/special-offers">Special Offers</Link>
              </Box> */}

              <Divider orientation="vertical" variant="middle" flexItem />

              <Box className="header-link">
                <Link href="/best-cruise-ships">Explore Ships</Link>
              </Box>

              <Divider orientation="vertical" variant="middle" flexItem />

              <Box className="header-link">
                <Link href="/ocean-cruises">Ocean Cruise Deals</Link>
              </Box>

              <Divider orientation="vertical" variant="middle" flexItem />

              <Box className="header-link">
                <Link href="/river-cruises">River Cruise Deals</Link>
              </Box>

              <Divider orientation="vertical" variant="middle" flexItem />

              <Box className="header-link">
                <Link href="/fly-cruise-deals">Fly-Cruise Deals</Link>
              </Box>

              <Divider orientation="vertical" variant="middle" flexItem />

              <Box className="header-link">
                <Link href="/cruise-holiday-deals">Cruise Deals</Link>
              </Box>

              {/* <Divider orientation="vertical" variant="middle" flexItem />

              <Box className="header-link">
                <Link href="/payment">Pay Online</Link>
              </Box> */}

              {/* <Box>Cruise Port</Box> */}
              {/* <Box>Cruise Consultation</Box> */}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "14px",
                transform: "scale(0.9) translateX(10px)",
                "@media screen and (max-width:1350px)": {
                  transform: "scale(1) translateX(0px)",
                },
              }}
            >
              <MailIconButton
                sx={{
                  display: "block",
                  "@media screen and (max-width:1100px)": {
                    display: "none",
                  },
                }}
              />

              {/* <WhatsAppButton
                sx={{
                  display: "block",
                  "@media screen and (max-width:1100px)": {
                    display: "none",
                  },
                }}
              /> */}

              <CallNowButton
                sx={{
                  display: "block",
                  "@media screen and (max-width:1100px)": {
                    display: "none",
                  },
                }}
              />

              <Button
                onClick={() => setMenuOpen(true)}
                aria-label="Open menu"
                sx={{
                  bgcolor: "primary.main",
                  color: "#fff",
                  borderRadius: "10px",
                  p: "4px 8px",
                  textTransform: "uppercase",
                  display: "none",
                  "@media screen and (max-width:1350px)": {
                    display: "flex",
                    ml: { xs: "0px", md: "8px" },
                  },
                  border: "3px solid #817DFF",
                  minWidth: "unset",
                  svg: {
                    width: "32px",
                    height: "32px",
                  },
                }}
              >
                <MenuIcon />
              </Button>
            </Box>
          </Box>
        </Container>
      </AppBar>

      <Box
        sx={{
          position: "fixed",
          boxShadow: "0px 0px 14px rgba(0,0,0,0.3)",
          bgcolor: "#fff",
          minHeight: "unset",
          height: { xs: "77px", md: "87px" },
          width: "100%",
          zIndex: 1098,
        }}
      ></Box>

      <Box sx={{ height: "77px", width: "100%" }}></Box>

      <Drawer
        anchor={isDesktop ? "right" : "bottom"}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        sx={{
          ".MuiPaper-root.MuiDrawer-paper": {
            bgcolor: "unset",
            maxHeight: { xs: "calc(100vh - 77px)", md: "100%" },
            maxWidth: { xs: "100%", md: "600px" },
          },
        }}
      >
        <Box
          sx={{
            p: "1rem",
            color: "#fff",
            bgcolor: "#030053",
            borderRadius: { xs: "20px 20px 0 0", md: "0px" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mb: "1.7rem",
            }}
          >
            {/* <Box sx={{ fontWeight: 700, fontSize: "24px" }}>Quick links</Box> */}

            <Button
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              sx={{
                bgcolor: "#fff",
                color: "primary.main",
                borderRadius: "10px",
                p: "3px 3px",
                textTransform: "uppercase",
                display: { xs: "flex", md: "none" },
                border: "3px solid #817DFF",
                minWidth: "unset",
                svg: {
                  width: "35px",
                  height: "35px",
                },
              }}
            >
              <CloseIcon />
            </Button>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr",
                xl: "1fr 1fr 1fr",
              },
              gap: "12px 10px",
              a: {
                textDecoration: "none",
              },
            }}
          >
            <FooterLinkButton
              onClick={() => setMenuOpen(false)}
              isMobileMenu
              label="Home"
              link="/"
            />
            <FooterLinkButton
              onClick={() => setMenuOpenDestination(true)}
              isMobileMenu
              label="Destinations"
            />
            <FooterLinkButton
              onClick={() => setMenuOpenCruiseline(true)}
              isMobileMenu
              label="Cruise Lines"
            />
            <FooterLinkButton
              onClick={() => setMenuOpen(false)}
              isMobileMenu
              label="Explore Ships"
              link="/best-cruise-ships"
            />

            <FooterLinkButton
              onClick={() => setMenuOpen(false)}
              isMobileMenu
              label="Ocean Cruise Deals"
              link="/ocean-cruises"
            />

            <FooterLinkButton
              onClick={() => setMenuOpen(false)}
              isMobileMenu
              label="River Cruise Deals"
              link="/river-cruises"
            />

            <FooterLinkButton
              onClick={() => setMenuOpen(false)}
              isMobileMenu
              label="Fly-Cruise Deals"
              link="/fly-cruise-deals"
            />

            <FooterLinkButton
              onClick={() => setMenuOpen(false)}
              isMobileMenu
              label="Cruise Deals"
              link="/cruise-holiday-deals"
            />

            <FooterLinkButton
              onClick={() => setMenuOpen(false)}
              isMobileMenu
              label="Pay Online"
              link="/payment"
            />
            <FooterLinkButton
              onClick={() => setMenuOpen(false)}
              isMobileMenu
              label="About Us"
              link="/about-us"
            />
            <FooterLinkButton
              onClick={() => setMenuOpen(false)}
              isMobileMenu
              label="Contact Us"
              link="/contact-us"
            />
            <FooterLinkButton
              onClick={() => setMenuOpen(false)}
              isMobileMenu
              label="Members Only"
              link="/members-only"
            />
            <FooterLinkButton
              onClick={() => setMenuOpen(false)}
              isMobileMenu
              label="Terms and Conditions"
              link="/help-and-support"
            />
            <FooterLinkButton
              onClick={() => setMenuOpen(false)}
              isMobileMenu
              label="Privacy Policy"
              link="/privacy-policy"
            />
            <FooterLinkButton
              onClick={() => setMenuOpen(false)}
              isMobileMenu
              label="Cookie Policy"
              link="/cookie-policy"
            />
          </Box>

          <Box
            sx={{
              mt: "2rem",
              mb: "1.5rem",
              textAlign: "center",
              fontFamily: "'blauerNue', 'blauerNue Fallback'",
              fontWeight: 600,
            }}
          >
            Need Help? Reach Out Instantly!
          </Box>

          <Box sx={{ mb: "2rem" }}>
            <HomeContactButtons />
          </Box>
        </Box>
      </Drawer>

      <Drawer
        anchor={"bottom"}
        open={menuOpenDestination}
        onClose={() => setMenuOpen(false)}
        sx={{
          ".MuiPaper-root.MuiDrawer-paper": {
            bgcolor: "unset",
            maxHeight: "calc(100vh - 77px)",
          },
        }}
      >
        <Box sx={{ p: "1rem", color: "#fff", bgcolor: "#030053", borderRadius: "20px 20px 0 0" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mb: "1.7rem",
            }}
          >
            <Button
              onClick={() => setMenuOpenDestination(false)}
              aria-label="Close menu"
              sx={{
                bgcolor: "#fff",
                color: "primary.main",
                borderRadius: "10px",
                p: "3px 3px",
                textTransform: "uppercase",
                display: { xs: "flex", md: "none" },
                border: "3px solid #817DFF",
                minWidth: "unset",
                svg: {
                  width: "35px",
                  height: "35px",
                },
              }}
            >
              <CloseIcon />
            </Button>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr",
                xl: "1fr 1fr 1fr",
              },
              gap: "12px 10px",
              a: {
                textDecoration: "none",
              },
            }}
          >
            {destinationList.map((item, index) => (
              <FooterLinkButton
                key={index}
                onClick={() => {
                  setMenuOpenDestination(false);
                  setMenuOpen(false);
                }}
                isMobileMenu
                label={item.name}
                link={`/cruise-destinations/${item.page_url}`}
              />
            ))}
            <FooterLinkButton
              onClick={() => {
                setMenuOpenDestination(false);
                setMenuOpen(false);
              }}
              isMobileMenu
              label="All Destinations"
              link="/cruise-destinations"
            />
          </Box>{" "}
        </Box>
      </Drawer>

      <Drawer
        anchor={"bottom"}
        open={menuOpenCruiseline}
        onClose={() => setMenuOpenCruiseline(false)}
        sx={{
          ".MuiPaper-root.MuiDrawer-paper": {
            bgcolor: "unset",
            maxHeight: "calc(100vh - 77px)",
          },
        }}
      >
        <Box sx={{ p: "1rem", color: "#fff", bgcolor: "#030053", borderRadius: "20px 20px 0 0" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mb: "1.7rem",
            }}
          >
            <Button
              onClick={() => setMenuOpenCruiseline(false)}
              aria-label="Close menu"
              sx={{
                bgcolor: "#fff",
                color: "primary.main",
                borderRadius: "10px",
                p: "3px 3px",
                textTransform: "uppercase",
                display: { xs: "flex", md: "none" },
                border: "3px solid #817DFF",
                minWidth: "unset",
                svg: {
                  width: "35px",
                  height: "35px",
                },
              }}
            >
              <CloseIcon />
            </Button>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
              },
              gap: "12px 10px",
              a: {
                textDecoration: "none",
              },
            }}
          >
            <>
              {(operatorData?.length ?? 0) > 0 ? (
                operatorData?.map((operator, index) => (
                  <Link
                    key={index}
                    href={`/best-cruise-lines/${operator?.id}`}
                    style={{ display: "grid" }}
                    onClick={() => {
                      setMenuOpenCruiseline(false);
                      setMenuOpen(false);
                    }}
                  >
                    <Box
                      sx={{
                        p: "8px",
                        bgcolor: "#060273",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "10px",
                        borderRadius: "10px",
                        border: "2px solid #817DFF",
                        svg: {
                          color: "#fff",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "20px",
                          width: "100%",
                        }}
                      >
                        <Box sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              fontSize: "16px",
                              fontWeight: 600,
                              fontFamily: "'blauerNue', 'blauerNue Fallback'",
                            }}
                          >
                            {operator?.title}{" "}
                          </Box>
                          <Box
                            sx={{
                              fontSize: "12px",
                              fontWeight: 400,
                              mt: "4px",
                              color: "#c1bfff",
                            }}
                          >
                            Operates {operator?.shipsCount} Ships
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            border: "1px solid #BFCDFF77",
                            p: "4px 6px",
                            bgcolor: "#fff",
                            borderRadius: "8px",
                            img: {
                              objectFit: "contain",
                              width: "120px",
                              height: "auto",
                              maxHeight: "50px",
                              borderRadius: "4px",
                              display: "flex",
                            },
                          }}
                        >
                          <Image
                            src={operator?.profileImageHref}
                            alt={operator?.title}
                            width={100}
                            height={50}
                            loader={({ src }) => src}
                          />
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          height: "100%",
                          borderRadius: "8px",
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        <ArrowOutwardIcon />
                      </Box>
                    </Box>
                  </Link>
                ))
              ) : (
                <Alert severity="error" sx={{ width: "400px" }}>
                  No data available
                </Alert>
              )}
            </>
          </Box>{" "}
        </Box>
      </Drawer>
    </>
  );
};

export default Header;

const MegaMenuSkeleton = () => {
  return (
    <Box
      sx={{
        p: "8px 15px 8px 12px",
        bgcolor: "#edf1ff",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "10px",
        position: "relative",
        borderRadius: "10px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "20px",
          width: "100%",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box sx={{ width: "100%", zIndex: 1 }}>
          <Skeleton
            variant="text"
            animation="wave"
            sx={{
              fontSize: "22px",
              width: "200px",
              borderRadius: "5px",
            }}
          />
          <Skeleton
            variant="text"
            animation="wave"
            sx={{
              fontSize: "12px",
              width: "100px",
              borderRadius: "2px",
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
            width: "100%",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{
              width: "120px",
              height: "40px",
              borderRadius: "4px",
            }}
          />
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{
              width: "30px",
              height: "30px",
              borderRadius: "4px",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
